// src/App.js
import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// Pages & Authentication Components
import Login from "./components/auth/Login";
import ForgotPassword from "./components/auth/ForgotPassword";
import RequestAccess from "./pages/RequestAccess";
import AdminSignup from "./pages/AdminSignup";

// Context Providers
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CompaniesProvider } from "./contexts/CompanyContext";
import { LocationsProvider } from "./contexts/LocationContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ThemeProvider } from "./contexts/ThemeContext";

// Zustand Data Store
import { useDataStore } from "./dbservice/useDataStore";

// Route Guards & Layouts
import PrivateRoute from "./components/common/PrivateRoute";
import RootRedirect from "./components/common/RootRedirect";
import Unauthorized from "./components/common/Unauthorized";
import SuperLayout from "./Layouts/SuperLayout";
import AdminLayout from "./Layouts/AdminLayout";
import UserLayout from "./Layouts/UserLayout";
import AuditorLayout from "./Layouts/AuditorLayout";

// Common Pages
import SettingsPage from "./components/common/SettingsPage";
import AssetDetailsPage from "./pages/AssetDetailsPage";
import AccessRequestsPanel from "./components/common/AccessRequestsPanel";
import MasterDataPage from "./pages/MasterDataPage";
import PublicAssetView from "./pages/PublicAssetView";
import FeedbackPage from "./components/common/Feedback/FeedbackPage";

// Error Boundary
import ErrorBoundary from "./components/common/ErrorBoundary";

// Super Admin Pages
import SuperAdminDashboard from "./components/superadmin/SuperAdminDashboard";
import CompanyManagement from "./components/superadmin/CompanyManagement";
import UserManagement from "./components/superadmin/UserManagement";

import SuperAdminReports from "./components/superadmin/SuperAdminReports";

import AuditLogs from "./components/superadmin/AuditLogs";
import PlatFormFeedback from "./components/superadmin/pages/PlatFormFeedback";

// Admin Pages
import AdminDashboard from "./components/admin/AdminDashboard";
import AdminReports from "./components/admin/AdminReports";
import AdminHelpDesk from "./components/admin/AdminHelpDesk";
import ChangeLocation from "./components/admin/ChangeLocation";
import AssetManagement from "./components/admin/AssetManagement";
import AdminKnowledgeBase from "./components/admin/AdminKnowledgeBase";

import Consumptions from "./components/admin/Consumptions"; 


import MyApprovalsPage from "./pages/MyApprovalsPage"; 

// User Pages
import UserDashboard from "./components/user/UserDashboard";
import AssignedAssets from "./components/user/AssignedAssets";
import UserViewTicket from "./components/user/UserViewTicket";
import UserHelpCenter from "./components/user/UserHelpCenter";

import ServerCheck from "./components/auth/ServerCheck";

import TechnicianLayout from "./Layouts/TechnicianLayout";
import TechnicianDashboard from "./components/technician/TechnicianDashboard";
import TechnicianTickets from "./components/technician/TechnicianTickets";
import TechnicianTicketDetail from "./components/technician/TechnicianTicketDetail";
import TechnicianHelp from "./components/technician/TechnicianHelp";

// Audit Pages
import AuditModule from "./components/audit/AuditModule";
import AuditorDashboard from "./components/audit/AuditorDashboard";
import AuditorHelp from "./components/audit/AuditorHelp";
 

// Utility Imports & Styling

import "bootstrap-icons/font/bootstrap-icons.css";
import "./output.css";

if (process.env.NODE_ENV === "development") {
  if (typeof process !== "undefined" && process.env) {
    process.env.NODE_TLS_REJECT_UNAUTHORIZED = "0";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CONDITIONAL ROUTE GUARD FOR SERVER CHECK
// ─────────────────────────────────────────────────────────────────────────────
const ServerCheckRoute = ({ element }) => {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (currentUser.role === "superadmin") {
    return <Navigate to="/super-admin" replace />;
  }

  return element;
};

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL DATA LIFECYCLE
// ─────────────────────────────────────────────────────────────────────────────
const AppDataWrapper = ({ children }) => {
  const { currentUser } = useAuth();
  const subscribeToCollection = useDataStore(
    (state) => state.subscribeToCollection,
  );
  const clearAllSubscriptions = useDataStore(
    (state) => state.clearAllSubscriptions,
  );
  const handleTabVisibilityLoss = useDataStore(
    (state) => state.handleTabVisibilityLoss,
  );

  // 1. Core Synchronization Pipeline
  useEffect(() => {
    if (!currentUser?.companyId) return;

    const filters = [{ field: "companyId", value: currentUser.companyId }];

     const syncPipelines = () => {
      console.log("Sync Engine: Re-establishing network lines...");
      subscribeToCollection("assets",       filters);
      subscribeToCollection("categories",   filters);
      subscribeToCollection("subCategories", filters);
      subscribeToCollection("brands",       filters);
      subscribeToCollection("vendors",      filters);
      subscribeToCollection("locations",    filters);
      subscribeToCollection("departments",  filters);
      subscribeToCollection("helpDeskIssues", filters);
    };
    
    syncPipelines();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        console.log("Niyantrix: Window minimized. Shutting down network pipes.");
        handleTabVisibilityLoss();
      } else if (document.visibilityState === "visible") {
        console.log("Niyantrix: Window restored. Reviving data feeds.");
        syncPipelines();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      console.log("Sync Engine: Cleaning up background tracking pipelines...");
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearAllSubscriptions();
    };
  }, [
    currentUser?.companyId,
    subscribeToCollection,
    clearAllSubscriptions,
    handleTabVisibilityLoss,
  ]);

  // 2. Warranty reminders are now scheduled directly with Brevo at
  //    asset-save time (see scheduleWarrantyReminders in brevoEmailService).
  //    No client-side polling loop is required.

  return children;
};

// ─────────────────────────────────────────────────────────────────────────────
// AUTHENTICATED APP CONTEXT LAYOUT
// ─────────────────────────────────────────────────────────────────────────────
const AuthenticatedApp = () => {
  return (
    <ErrorBoundary fallback={null}>
      <CompaniesProvider>
        <LocationsProvider>
          <ToastProvider>
            <ThemeProvider>
              <AppDataWrapper>
                <Routes>

                  <Route
                    path="/server-check"
                    element={<ServerCheckRoute element={<ServerCheck />} />}
                  />

                  {/* Asset Details - Accessible to all authenticated roles */}
                  <Route
                    path="/asset-details/:assetId"
                    element={
                      <PrivateRoute
                        roles={[
                          "superadmin",
                          "locationAdmin",
                          "admin",
                          "user",
                          "auditor",
                          "technician",
                        ]}
                      >
                        <AssetDetailsPage />
                      </PrivateRoute>
                    }
                  />

                  {/* ── Super Admin Routes ── */}
                  <Route
                    path="/super-admin/*"
                    element={
                      <PrivateRoute roles={["superadmin"]}>
                        <SuperLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index                    element={<SuperAdminDashboard />} />
                    <Route path="dashboard"         element={<SuperAdminDashboard />} />
                    <Route path="users"             element={<UserManagement />}      />
                    
                    <Route path="masters/companies" element={<CompanyManagement />}   />
                    <Route path="access-requests"   element={<AccessRequestsPanel />} />
                    <Route path="reports"           element={<SuperAdminReports />}   />
                    <Route path="audit-logs"        element={<AuditLogs />}           />
                    <Route path="feedback"          element={<PlatFormFeedback />}    />
                    <Route path="settings"          element={<SettingsPage />}        />
                  </Route>

                  {/* ── Admin Routes ── */}
                  <Route
                    path="/admin/*"
                    element={
                      <PrivateRoute roles={["admin", "locationAdmin"]}>
                        <AdminLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index                  element={<AdminDashboard />}      />
                    <Route path="dashboard"       element={<AdminDashboard />}      />
                    <Route path="users"           element={<UserManagement />}      />
                    <Route path="assets"          element={<AssetManagement />}     />
                   <Route path="reports" element={<Navigate to="assetRegistry" replace />} />
  <Route path="reports/:reportType" element={<AdminReports />} />
                     <Route path="my-approvals"    element={<MyApprovalsPage />}     />
                    <Route path="access-requests" element={<AccessRequestsPanel />} />
                    <Route path="master-data"     element={<MasterDataPage />}      />
                    <Route path="help-desk"       element={<AdminHelpDesk />}       />
                         
                    <Route path="knowledge-base"  element={<AdminKnowledgeBase />}  />
                    <Route path="feedback"        element={<FeedbackPage />}        />
                    <Route path="settings"        element={<SettingsPage />}        />
                    <Route path="change-location" element={<ChangeLocation />}      />
                    <Route path="audit"           element={<AuditModule />}         />

                    {/* ── NEW: Consumptions route ── */}
                    <Route path="consumptions"    element={<Consumptions />}        />

                  </Route>

                  {/* ── User Routes ── */}
                  <Route
                    path="/user/*"
                    element={
                      <PrivateRoute roles={["user"]}>
                        <UserLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index                  element={<UserDashboard />}   />
                    <Route path="dashboard"       element={<UserDashboard />}   />
                    <Route path="assignedassets"  element={<AssignedAssets />}  />
                         
                    <Route path="help-center"     element={<UserHelpCenter />}  />
                    <Route path="feedback"        element={<FeedbackPage />}    />
                    <Route path="settings"        element={<SettingsPage />}    />
                    <Route path="tickets"         element={<UserViewTicket />}  />
                      <Route path="my-approvals"    element={<MyApprovalsPage />} />
                  
                  </Route>
{/* ── Technician Routes ── */}
<Route
  path="/technician/*"
  element={
    <PrivateRoute roles={["technician"]}>
      <TechnicianLayout />
    </PrivateRoute>
  }
>
  <Route index               element={<TechnicianDashboard />}     />
  <Route path="dashboard"    element={<TechnicianDashboard />}     />
  <Route path="tickets"      element={<TechnicianTickets />}       />
  <Route path="tickets/:ticketId" element={<TechnicianTicketDetail />} />
  <Route path="help"         element={<TechnicianHelp />}          />
  <Route path="settings"     element={<SettingsPage />}            />
</Route>
                  {/* ── Auditor Routes ── */}
                  <Route
                    path="/auditor/*"
                    element={
                      <PrivateRoute roles={["auditor"]}>
                        <AuditorLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index                  element={<AuditorDashboard />}                   />
                    <Route path="dashboard"       element={<AuditorDashboard />}                   />
                    <Route path="active-audits"   element={<AuditModule />}                        />
                                      <Route path="my-approvals"    element={<MyApprovalsPage />}                    />
                    <Route path="help"            element={<AuditorHelp />}                        />
                    <Route path="settings"        element={<SettingsPage />}                       />
                    <Route path="tickets"         element={<UserViewTicket role="auditor" />}      />
                  </Route>

                  {/* ── Catch-alls ── */}
                  <Route path="/"  element={<RootRedirect />}                  />
                  <Route path="*"  element={<Navigate to="/login" replace />}  />

                </Routes>
              </AppDataWrapper>
            </ThemeProvider>
          </ToastProvider>
        </LocationsProvider>
      </CompaniesProvider>
    </ErrorBoundary>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ROOT APPLICATION ENTRY POINT
// ─────────────────────────────────────────────────────────────────────────────
function App() {
  useEffect(() => {
    document.title = "Niyantrix - Asset Management";
  }, []);

  return (
    <AuthProvider>
      <Routes>
        {/* Completely Public Unauthenticated Routes */}
        <Route path="/public-asset/:assetId" element={<PublicAssetView />} />
        <Route path="/login"                 element={<Login />}           />
        <Route path="/forgot-password"       element={<ForgotPassword />}  />
        <Route path="/unauthorized"          element={<Unauthorized />}    />
        <Route path="/request-access"        element={<RequestAccess />}   />
        <Route path="/admin-signup"          element={<AdminSignup />}     />

        {/* Authenticated Dynamic Application Segment */}
        <Route path="/*" element={<AuthenticatedApp />} />
      </Routes>
    </AuthProvider>
  );
}

export default App;